<template>
    <div class="report-prep">
        <div class="report-prep__header">
            <h1 class="report-prep__title">{{ reportName }}</h1>
        </div>

        <div class="report-prep__content">
            <!-- Selection Section -->
            <div class="report-prep__section">
                <div class="report-prep__section-header">
                    <h2 class="report-prep__section-title">{{ selectionTitle }}</h2>
                    <p class="report-prep__section-description">
                        {{ selectionDescription }}
                    </p>
                </div>

                <div class="report-prep__site-selector">
                    <themed-multi-select
                        v-model="selectedItems"
                        :options="itemOptions"
                        :placeholder="selectionPlaceholder"
                    />
                </div>
            </div>

            <!-- Include Options (if provided) -->
            <div v-if="hasIncludeOptions" class="report-prep__section">
                <div class="report-prep__section-header">
                    <h2 class="report-prep__section-title">Include</h2>
                    <p class="report-prep__section-description">
                        Choose which sections to include in the report
                    </p>
                </div>

                <div class="report-prep__options">
                    <div 
                        v-for="option in includeOptionsList" 
                        :key="option.key"
                        class="report-prep__option"
                    >
                        <themed-checkbox
                            v-model="includeOptions[option.key]"
                            :label="option.label"
                        />
                    </div>
                </div>
            </div>

            <!-- Generate Button -->
            <div class="report-prep__actions">
                <themed-button
                    @click="generateReport"
                    variant="default"
                    size="lg"
                    :disabled="selectedItems.length === 0"
                >
                    Generate Report
                </themed-button>
            </div>
        </div>
    </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedCheckbox from '../ThemedCheckbox.vue';
import ThemedMultiSelect from '../ThemedMultiSelect.vue';

export default {
    name: 'SiteReportPrep',
    
    components: {
        ThemedButton,
        ThemedCheckbox,
        ThemedMultiSelect
    },

    props: {
        reportName: {
            type: String,
            required: true
        },
        reportSlug: {
            type: String,
            required: true
        },
        reportType: {
            type: String,
            default: 'site' // 'site' or 'project'
        },
        items: {
            type: Array,
            default: () => []
        },
        includeOptionsList: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            selectedItems: [],
            includeOptions: {}
        };
    },

    created() {
        // Initialize include options
        this.includeOptionsList.forEach(option => {
            this.$set(this.includeOptions, option.key, option.default !== false);
        });
    },

    computed: {
        itemOptions() {
            if (this.reportType === 'project') {
                return this.items.map(item => ({
                    value: item.id,
                    label: item.name || 'Unknown Project'
                }));
            }
            // Default to site
            return this.items.map(item => ({
                value: item.id,
                label: item.building?.address_line_1 || 'Unknown Address'
            }));
        },

        selectionTitle() {
            return this.reportType === 'project' ? 'Select Projects' : 'Select Sites';
        },

        selectionDescription() {
            return this.reportType === 'project' 
                ? 'Select one or multiple projects to include in the report'
                : 'Select one or multiple sites to include in the report';
        },

        selectionPlaceholder() {
            return this.reportType === 'project' ? 'Select projects...' : 'Select sites...';
        },

        hasIncludeOptions() {
            return this.includeOptionsList && this.includeOptionsList.length > 0;
        }
    },

    methods: {
        generateReport() {
            if (this.selectedItems.length === 0) {
                this.$notify({
                    type: 'warning',
                    title: `No ${this.reportType === 'project' ? 'Projects' : 'Sites'} Selected`,
                    text: `Please select at least one ${this.reportType} to generate the report.`
                });
                return;
            }

            const filters = {};
            
            // Add IDs based on report type
            if (this.reportType === 'project') {
                filters.projectIDs = this.selectedItems;
            } else {
                filters.siteIDs = this.selectedItems;
            }

            // Add include options
            Object.keys(this.includeOptions).forEach(key => {
                filters[`include_${key}`] = this.includeOptions[key];
            });

            const encodedFilters = btoa(JSON.stringify(filters));
            window.location = `/reports/generate/${this.reportSlug}?filters=${encodedFilters}`;
        }
    }
};
</script>
